import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, WalletIcon, TrendingUpIcon } from 'lucide-react';
interface WelcomeProps {
  navigate: (screen: string) => void;
}
export function Welcome({
  navigate
}: WelcomeProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const slides = [{
    id: 0,
    title: 'Save Towards Any Goal',
    subtitle: "Set a target, we'll help you hit it - with in-built protection",
    icon: TrendingUpIcon,
    color: 'emerald',
    mascot: 'https://cdn.magicpatterns.com/uploads/fCtTe1ZG3m1ZsjnpHbbpZx/DD_standing_front_1.png'
  }, {
    id: 1,
    title: 'Your Goals, Guaranteed',
    subtitle: 'If life takes an unexpected turn, the full target amount is paid out to your beneficiary immediately. No questions asked.',
    icon: ShieldCheckIcon,
    color: 'amber',
    mascot: 'https://cdn.magicpatterns.com/uploads/twohoUfqTs4sUfnNQ4Jbt5/DD_with_umbrella_1.png'
  }, {
    id: 2,
    title: 'All Your Finances, One App',
    subtitle: 'Manage your savings, digital wallet, cards, and protection plans seamlessly in one unified ecosystem.',
    icon: WalletIcon,
    color: 'indigo',
    mascot: 'https://cdn.magicpatterns.com/uploads/ddGEqtHHBLaRgK5q4UKEDw/DD_winking_1.png'
  }];
  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const width = scrollRef.current.offsetWidth;
      const newIndex = Math.round(scrollLeft / width);
      if (newIndex !== currentSlide) {
        setCurrentSlide(newIndex);
      }
    }
  };
  const scrollToSlide = (index: number) => {
    if (scrollRef.current) {
      const width = scrollRef.current.offsetWidth;
      scrollRef.current.scrollTo({
        left: width * index,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };
  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      scrollToSlide(currentSlide + 1);
    } else {
      navigate('login');
    }
  };
  return <div className="flex-1 bg-[#0D3B3B] flex flex-col h-full relative overflow-hidden" data-id="element-3251">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" data-id="element-3252" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2BA89D]/20 rounded-full blur-3xl -ml-16 -mb-16 pointer-events-none" data-id="element-3253" />

      {/* Skip Button */}
      <div className="absolute top-safe right-5 z-20 pt-4" data-id="element-3254">
        <AnimatePresence data-id="element-3255">
          {currentSlide < slides.length - 1 && <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => navigate('login')} className="text-white/60 text-sm font-medium px-4 py-2 rounded-full hover:bg-white/10 transition-colors" data-id="element-3256">
              Skip
            </motion.button>}
        </AnimatePresence>
      </div>

      {/* Slides Container */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 flex overflow-x-auto snap-x snap-mandatory scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-3257">
        {slides.map((slide, index) => <div key={slide.id} className="min-w-full h-full snap-center flex flex-col items-center justify-center px-8 relative pt-12 pb-32" data-id="element-3258">
            {/* Mascot Image */}
            <motion.div initial={{
          scale: 0.8,
          opacity: 0,
          y: 20
        }} animate={{
          scale: currentSlide === index ? 1 : 0.8,
          opacity: currentSlide === index ? 1 : 0,
          y: currentSlide === index ? 0 : 20
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative w-56 h-56 mb-12 flex items-center justify-center" data-id="element-3259">
              <div className="absolute inset-0 bg-[#FFD700]/20 rounded-full blur-3xl" data-id="element-3260" />
              <img src={slide.mascot} alt="Skydd Mascot" className="w-full h-full object-contain relative z-10 drop-shadow-2xl" data-id="element-3261" />

              {/* Floating Icon */}
              <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -right-4 -top-4 w-14 h-14 bg-[#FFD700] rounded-2xl shadow-xl flex items-center justify-center text-[#0D3B3B] rotate-12 z-20" data-id="element-3262">
                <slide.icon className="w-7 h-7" data-id="element-3263" />
              </motion.div>
            </motion.div>

            {/* Text Content */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: currentSlide === index ? 1 : 0,
          y: currentSlide === index ? 0 : 20
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-center w-full max-w-sm" data-id="element-3264">
              <h1 className="text-3xl font-black text-white mb-4 leading-tight" data-id="element-3265">
                {slide.title}
              </h1>
              <p className="text-white/70 text-base leading-relaxed font-medium" data-id="element-3266">
                {slide.subtitle}
              </p>
            </motion.div>
          </div>)}
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-8 pb-safe z-20 bg-gradient-to-t from-[#0D3B3B] via-[#0D3B3B] to-transparent pt-20" data-id="element-3267">
        <div className="flex flex-col items-center gap-8 max-w-sm mx-auto w-full" data-id="element-3268">
          {/* Dots */}
          <div className="flex gap-2.5" data-id="element-3269">
            {slides.map((_, idx) => <button key={idx} onClick={() => scrollToSlide(idx)} className={`h-2 rounded-full transition-all duration-300 ${currentSlide === idx ? 'w-8 bg-[#FFD700]' : 'w-2 bg-white/20 hover:bg-white/40'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-3270" />)}
          </div>

          {/* Action Button */}
          <button onClick={nextSlide} className="w-full h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 bg-[#FFD700] text-[#0D3B3B] hover:bg-[#F0C900] shadow-xl shadow-amber-500/20" data-id="element-3271">
            {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
            {currentSlide === slides.length - 1 && <ArrowRightIcon className="w-5 h-5" data-id="element-3272" />}
          </button>
        </div>
      </div>
    </div>;
}